import { ValidatorFn, Validators } from '@angular/forms';
import {
  CheckoutExtensionCommonConfig,
  CheckoutExtensionUI,
  CustomUpsellExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { RuleEntity } from '@pdqdev/pdq-shared/dist/entities/Rules';
import { BORDER_OPTIONS, CORNER_RADIUS_OPTIONS, DropdownOption } from '../../shared/config/extension-ui-options';
import PlacementMode = CheckoutExtensionCommonConfig.PlacementMode;

export type AutoAddMode = 'once' | 'persistent';
export type AutoAddScope = 'freeOnly' | 'all';
export type AutoAddPersistentCheckbox = 'disabled' | 'hidden';

export interface CustomUpsellAutoAdd {
  enabled: boolean;
  mode: AutoAddMode;
  scope: AutoAddScope;
  persistentCheckbox: AutoAddPersistentCheckbox;
}

export const AUTO_ADD_DEFAULTS: CustomUpsellAutoAdd = {
  enabled: false,
  mode: 'once',
  scope: 'freeOnly',
  persistentCheckbox: 'disabled',
};

export interface ExtensionConfig {
  fieldName: string;
  defaultValue?: any;
  type?: 'formArray' | 'fieldset' | 'dynamic-ruleset';
  validators?: Validators[] | ValidatorFn[];
  fields?: { fieldName: string; defaultValue: any; validators?: Validators[] | ValidatorFn[] }[];
  rulesConfig?: {
    entity: RuleEntity;
    entityParams: {
      required?: boolean;
      name: string;
      defaultValue: string;
    }[];
  }[];
}

export const CustomUpsellConfig: ExtensionConfig[] = [
  {
    fieldName: 'internalName',
    defaultValue: 'Digital upsell',
  },
  {
    fieldName: 'title',
    defaultValue: 'Priority processing',
    validators: [Validators.required],
  },
  {
    fieldName: 'description',
    defaultValue: 'Get special service for your order',
  },
  {
    fieldName: 'upsellRate',
    defaultValue: '6',
  },
  {
    fieldName: 'discount',
    defaultValue: '20',
  },
  {
    fieldName: 'placementMode',
    defaultValue: PlacementMode.STANDALONE,
  },
  {
    fieldName: 'prioritizeOrder',
    defaultValue: false,
  },
  {
    fieldName: 'customTag',
    defaultValue: null,
  },
  {
    fieldName: 'productImageBase64',
    defaultValue: '',
  },
  {
    fieldName: 'upsellRateType',
    defaultValue: CustomUpsellExtension.UpsellRateType.FIXED,
  },
  {
    fieldName: 'upsellRatePercentage',
    defaultValue: '5',
  },
  {
    fieldName: 'applyDiscountTo',
    defaultValue: 'upsellItem',
  },
  {
    fieldName: 'usageToken',
    defaultValue: '',
  },
  {
    fieldName: 'tooltipEnabled',
    defaultValue: false,
  },
  {
    fieldName: 'tooltipTitle',
    defaultValue: 'Popup title',
  },
  {
    fieldName: 'tooltipText',
    defaultValue: 'Popup description',
  },
  {
    fieldName: 'autoAdd',
    type: 'fieldset',
    fields: [
      { fieldName: 'enabled', defaultValue: AUTO_ADD_DEFAULTS.enabled },
      { fieldName: 'mode', defaultValue: AUTO_ADD_DEFAULTS.mode },
      { fieldName: 'scope', defaultValue: AUTO_ADD_DEFAULTS.scope },
      { fieldName: 'persistentCheckbox', defaultValue: AUTO_ADD_DEFAULTS.persistentCheckbox },
    ],
  },
  {
    fieldName: 'minCartValueThreshold',
    defaultValue: null,
  },
  {
    fieldName: 'maxCartValueThreshold',
    defaultValue: null,
  },
  {
    fieldName: 'customProductConfiguration',
    type: 'fieldset',
    fields: [
      {
        fieldName: 'enabled',
        defaultValue: false,
      },
      {
        fieldName: 'numOfProducts',
        defaultValue: 1,
      },
      {
        fieldName: 'variantsPrecision',
        defaultValue: 0.75,
      },
    ],
  },
  {
    fieldName: 'restrictionsRules',
    type: 'dynamic-ruleset',
    defaultValue: [],
    rulesConfig: getExtensionRules(),
  },
  {
    fieldName: 'keepInCart',
    defaultValue: false,
  },
  {
    fieldName: 'infoPopupImage',
    defaultValue: '',
  },
];

export const TRUST_SIGNAL_ICON_OPTIONS: { value: string; label: string }[] = [
  { value: 'verified', label: 'Verified' },
  { value: 'calendar', label: 'On time' },
  { value: 'box', label: 'Delivery' },
  { value: 'dollar', label: 'Money-back' },
];

export const TRUST_SIGNALS_LAYOUT_OPTIONS: { text: string; value: CustomUpsellExtension.TrustSignalsLayout }[] = [
  { text: 'Vertical', value: CustomUpsellExtension.TrustSignalsLayout.VERTICAL },
  { text: 'Horizontal', value: CustomUpsellExtension.TrustSignalsLayout.HORIZONTAL },
];

export const TRUST_SIGNALS_DEFAULT_BACKGROUND = 'transparent';
export const TRUST_SIGNALS_DEFAULT_BORDER = CheckoutExtensionUI.BorderType.NONE;
export const TRUST_SIGNALS_DEFAULT_CORNER_RADIUS = CheckoutExtensionUI.CornerRadius.BASE;

export const TRUST_SIGNALS_BACKGROUND_OPTIONS: DropdownOption[] = [
  { label: 'None', value: 'transparent' },
  { label: 'Base', value: 'base' },
  { label: 'Subdued', value: 'subdued' },
];

export const TRUST_SIGNALS_BORDER_OPTIONS: DropdownOption[] = BORDER_OPTIONS;

export const TRUST_SIGNALS_CORNER_OPTIONS: DropdownOption[] = CORNER_RADIUS_OPTIONS.filter(
  (option) => option.value !== CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED,
);

export const UpsellRateTypeConfig = [
  {
    text: 'Fixed rate',
    value: CustomUpsellExtension.UpsellRateType.FIXED,
    defaultChecked: true,
    checked: (val: string) => {
      return val === CustomUpsellExtension.UpsellRateType.FIXED;
    },
    disabled: (val: boolean): boolean => !!val,
  },
  {
    text: 'Percent',
    value: CustomUpsellExtension.UpsellRateType.PERCENT,
    defaultChecked: false,
    checked: (val: string) => {
      return val === CustomUpsellExtension.UpsellRateType.PERCENT;
    },
    disabled: (val: boolean): boolean => !!val,
  },
];
